/**
 * Les effets sonores du jeu — synthetises, aucun fichier charge.
 *
 * Coherent avec la regle de contenu du depot (« rien sous licence ») : une
 * bibliotheque de sons libres devrait quand meme etre sourcee, versionnee,
 * livree. Cinq lignes de Web Audio API n'ont besoin d'aucune de ces trois
 * choses, et donnent exactement le grain chiptune qui va avec le style pixel.
 *
 * Le contexte audio est cree PARESSEUSEMENT, au premier son demande — jamais
 * au chargement de la page. Les navigateurs bloquent l'audio avant tout
 * geste de l'utilisateur ; créer le contexte trop tot le laisserait suspendu
 * pour de bon sur certains moteurs.
 */

type WaveShape = OscillatorType;

interface Tone {
  freq: number;
  /** Duree en secondes. */
  duration: number;
  shape?: WaveShape;
  /** Volume relatif, 0 a 1. */
  gain?: number;
  /** Decalage de depart, en secondes, pour enchainer plusieurs notes. */
  at?: number;
}

let ctx: AudioContext | null = null;
let muted = false;

function context(): AudioContext | null {
  // Pas d'audio sans navigateur (tests), et pas d'echec bruyant si l'API
  // manque : un jeu ne doit jamais planter parce qu'il ne peut pas biper.
  if (typeof window === 'undefined') return null;
  const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return null;
  if (!ctx) ctx = new Ctor();
  if (ctx.state === 'suspended') void ctx.resume();
  return ctx;
}

/** Une note : oscillateur + enveloppe d'amplitude, pour eviter les clics secs. */
function playTone(audio: AudioContext, tone: Tone): void {
  const start = audio.currentTime + (tone.at ?? 0);
  const osc = audio.createOscillator();
  const gain = audio.createGain();
  osc.type = tone.shape ?? 'square';
  osc.frequency.setValueAtTime(tone.freq, start);

  const peak = (tone.gain ?? 0.18) * 0.5; // le volume global reduit tout d'un coup
  gain.gain.setValueAtTime(0, start);
  gain.gain.linearRampToValueAtTime(peak, start + 0.008); // attaque quasi immediate
  gain.gain.exponentialRampToValueAtTime(0.0001, start + tone.duration); // chute douce

  osc.connect(gain).connect(audio.destination);
  osc.start(start);
  osc.stop(start + tone.duration + 0.02);
}

function play(tones: Tone[]): void {
  if (muted) return;
  const audio = context();
  if (!audio) return;
  for (const tone of tones) playTone(audio, tone);
}

/** Une rampe de frequence, pour un bruitage qui glisse (buzz, chute). */
function playSweep(from: number, to: number, duration: number, shape: WaveShape = 'sawtooth'): void {
  if (muted) return;
  const audio = context();
  if (!audio) return;
  const start = audio.currentTime;
  const osc = audio.createOscillator();
  const gain = audio.createGain();
  osc.type = shape;
  osc.frequency.setValueAtTime(from, start);
  osc.frequency.exponentialRampToValueAtTime(Math.max(1, to), start + duration);

  gain.gain.setValueAtTime(0, start);
  gain.gain.linearRampToValueAtTime(0.1, start + 0.01);
  gain.gain.exponentialRampToValueAtTime(0.0001, start + duration);

  osc.connect(gain).connect(audio.destination);
  osc.start(start);
  osc.stop(start + duration + 0.02);
}

export const sfx = {
  setMuted(value: boolean): void {
    muted = value;
  },
  isMuted(): boolean {
    return muted;
  },

  /** On presse le buzzer : une montee agressive, ca doit claquer. */
  buzz(): void {
    playSweep(220, 880, 0.14, 'sawtooth');
  },

  /** Un nouveau palier d'indice se revele. Discret : ca arrive souvent. */
  reveal(): void {
    play([{ freq: 880, duration: 0.06, shape: 'sine', gain: 0.1 }]);
  },

  /** Bonne reponse : un petit arpege majeur, la recompense classique. */
  correct(): void {
    play([
      { freq: 523.25, duration: 0.12, at: 0, gain: 0.16 },
      { freq: 659.25, duration: 0.12, at: 0.07, gain: 0.16 },
      { freq: 783.99, duration: 0.18, at: 0.14, gain: 0.18 },
    ]);
  },

  /** Mauvaise reponse : ca descend, court et net. */
  wrong(): void {
    playSweep(300, 90, 0.22, 'square');
  },

  /** La cagnotte encaisse. */
  chipGain(): void {
    play([
      { freq: 660, duration: 0.05, at: 0, gain: 0.12 },
      { freq: 990, duration: 0.08, at: 0.05, gain: 0.14 },
    ]);
  },

  /** La cagnotte perd. Un seul coup, sourd. */
  chipLoss(): void {
    play([{ freq: 140, duration: 0.16, shape: 'square', gain: 0.14 }]);
  },

  /** Un pouvoir se declare ou se resout. */
  power(): void {
    play([
      { freq: 440, duration: 0.05, at: 0, gain: 0.12 },
      { freq: 220, duration: 0.1, at: 0.05, gain: 0.14, shape: 'sawtooth' },
    ]);
  },

  /** Un souffle du chrono, sous le dernier quart de temps. */
  tick(): void {
    play([{ freq: 1_200, duration: 0.03, shape: 'square', gain: 0.06 }]);
  },

  /** Un de tombe. */
  diceRoll(): void {
    playSweep(300, 150, 0.1, 'triangle');
  },
};
